import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';

import './index.less';

import IbIcon from '../common/IbIcon';
import IbButton from '../common/IbButton';
import IbTypography from '../common/IbTypography';
import IbTooltip from '../common/IbTooltip';
import IbSortButton, { ISortMode } from '../common/IbSortButton';
import IbDebounceInput from '../common/IbDebounceInput';

const MAIN_CLASS_NAME = 'ib-page-title';
const TITLE_ROWS_CLASS_NAME = `${MAIN_CLASS_NAME}__title-rows`;
const BREADCRUMBS_CLASS_NAME = `${TITLE_ROWS_CLASS_NAME}__breadcrumbs`;
const BREADCRUMBS_SKELETON_CLASS_NAME = `${BREADCRUMBS_CLASS_NAME}__skeleton`;
const HEADER_CLASS_NAME = `${TITLE_ROWS_CLASS_NAME}__header`;
const HEADER_SKELETON_CLASS_NAME = `${HEADER_CLASS_NAME}__skeleton`;
const CONTENT_CLASS_NAME = `${MAIN_CLASS_NAME}__content`;
const CONTENT_DESKTOP_CLASS_NAME = `${CONTENT_CLASS_NAME}_desktop`;
const CONTENT_MOBILE_CLASS_NAME = `${CONTENT_CLASS_NAME}_mobile`;
const SEARCH_CLASS_NAME = `${MAIN_CLASS_NAME}__search`;

const BREADCRUMBS_SKELETON_HEIGHT = 20;
const BREADCRUMBS_SKELETON_WIDTH = 240;
const HEADER_SKELETON_HEIGHT = 32;
const HEADER_SKELETON_WIDTH = 384;

export interface IBreadcrumb {
  title: string;
  path: string;
}

export interface IIbPageTitleProps {
  loading?: boolean;
  title: ReactNode;
  showSearchInput?: boolean;
  searchPlaceholder?: string;
  hideTagsFiltering?: boolean;
  accountWidget?: ReactNode;
  desktopContent?: ReactNode;
  mobileContent?: ReactNode;
  sortModes?: ISortMode[];
  selectedSortMode?: string;
  breadcrumbs?: IBreadcrumb[];
  searchText?: string;
  onDebouncedSearch?: (value: string) => void;
  onSortModeChanged?: (value: string) => void;
  onAdd?: () => void;
}

const IbPageTitle: React.FC<IIbPageTitleProps> = ({
  loading = false,
  title,
  showSearchInput,
  searchPlaceholder,
  accountWidget,
  desktopContent,
  mobileContent,
  sortModes,
  selectedSortMode,
  breadcrumbs,
  searchText,
  onDebouncedSearch,
  onSortModeChanged,
  onAdd,
}) => {
  const hasBreadcrumbs = breadcrumbs?.length;

  const renderBreadcrumbs = () => {
    if (!hasBreadcrumbs) {
      return null;
    }

    if (loading) {
      return (
        <Skeleton
          containerClassName={BREADCRUMBS_SKELETON_CLASS_NAME}
          height={BREADCRUMBS_SKELETON_HEIGHT}
          width={BREADCRUMBS_SKELETON_WIDTH}
        />
      );
    }
    return (
      <div className={BREADCRUMBS_CLASS_NAME}>
        {(breadcrumbs ?? []).map((item, index) => (
          <React.Fragment key={item.title}>
            {index > 0 && <IbIcon iconName="right" size={16} />}
            <Link to={{ pathname: item.path }}>{item.title}</Link>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderHeader = () => {
    if (loading) {
      return (
        <Skeleton
          containerClassName={HEADER_SKELETON_CLASS_NAME}
          height={HEADER_SKELETON_HEIGHT}
          width={HEADER_SKELETON_WIDTH}
        />
      );
    }

    return (
      <div className={HEADER_CLASS_NAME}>
        <IbTooltip title={typeof title === 'string' ? title : undefined}>
          <IbTypography.Paragraph ellipsis strong>
            {title}
          </IbTypography.Paragraph>
        </IbTooltip>
        {onAdd && <IbButton icon={<IbIcon iconName="add-one" />} type="link" onClick={onAdd} />}
      </div>
    );
  };

  return (
    <IbTypography className={MAIN_CLASS_NAME}>
      {accountWidget}
      <div className={TITLE_ROWS_CLASS_NAME}>
        {renderBreadcrumbs()}
        {renderHeader()}
      </div>
      <div className={`${CONTENT_CLASS_NAME} ${CONTENT_DESKTOP_CLASS_NAME}`}>{desktopContent}</div>
      <div className={`${CONTENT_CLASS_NAME} ${CONTENT_MOBILE_CLASS_NAME}`}>{mobileContent}</div>
      {!!showSearchInput && (
        <div className={SEARCH_CLASS_NAME}>
          <IbDebounceInput
            allowClear
            placeholder={searchPlaceholder}
            prefix={<IbIcon iconName="search" />}
            value={searchText}
            onDebouncedChange={onDebouncedSearch}
          />
        </div>
      )}
      {!!showSearchInput && !!sortModes?.length && (
        <IbSortButton options={sortModes} value={selectedSortMode} onChange={onSortModeChanged} />
      )}
    </IbTypography>
  );
};

export default IbPageTitle;
